import React from 'react';
import { FieldArray, useFormikContext } from 'formik';

import Button from '~/components/core/Atomic/Buttons/Button';
import MenuItem from '~/components/core/Atomic/MenuItem';
import Text from '~/components/core/TextComponents/Text';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import { TrashIcon } from '~/components/icons';
import InlineIconButtonUntyped from '~/components/InlineIconButton';
import TextFieldFormik from '~/components/TextFieldFormik';

import TEXTS from './texts';
import type { ConfigFormValues } from './types';
import { emptyHeaderRow } from './types';

// InlineIconButton is a .jsx file; under allowJs its optional props (including `ariaLabel`) are
// inferred as the literal `undefined` type. Cast to the runtime shape so a real label can be passed.
interface InlineIconButtonProps {
  icon: React.ElementType;
  tooltipTitle?: string;
  ariaLabel?: string;
  onClick?: () => void;
  disabled?: boolean;
}
const InlineIconButton = InlineIconButtonUntyped as React.ComponentType<InlineIconButtonProps>;

interface ExtraHeadersEditorProps {
  isEdit: boolean;
  // Whether the persisted config already has extra headers. Undefined when the backend does not
  // report it, in which case no "configured" hint is shown.
  hasExtraHeaders?: boolean;
}

const ExtraHeadersEditor: React.FC<ExtraHeadersEditorProps> = ({ isEdit, hasExtraHeaders }) => {
  const { values } = useFormikContext<ConfigFormValues>();
  const mode = values.extra_headers_mode;

  const modeOptions: { value: string; label: string }[] = [
    { value: 'keep', label: isEdit ? TEXTS.extraHeaders.modeKeepEdit : TEXTS.extraHeaders.modeKeepCreate },
    { value: 'replace', label: TEXTS.extraHeaders.modeReplace },
    { value: 'clear', label: TEXTS.extraHeaders.modeClear },
  ];

  return (
    <div className="flex flex-col gap-8">
      <Text variant={Text.VARIANTS.XS}>{TEXTS.extraHeaders.helper}</Text>
      {isEdit && hasExtraHeaders && (
        <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.SEMI_BOLD}>
          {TEXTS.extraHeaders.configured}
        </Text>
      )}
      <TextFieldFormik select id="extra_headers_mode" name="extra_headers_mode" label={TEXTS.extraHeaders.mode}>
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore TextFieldFormik (JS) is not typed for select children */}
        {modeOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextFieldFormik>

      {mode === 'replace' && (
        <FieldArray
          name="extra_headers"
          render={({ push, remove }) => (
            <div className="flex flex-col gap-8">
              {values.extra_headers.map((_, idx) => (
                <div className="flex items-center gap-8" key={idx}>
                  <TextFieldFormik
                    id={`extra_headers.${idx}.name`}
                    name={`extra_headers.${idx}.name`}
                    label={TEXTS.extraHeaders.headerName}
                    fullWidth
                  />
                  <TextFieldFormik
                    id={`extra_headers.${idx}.value`}
                    name={`extra_headers.${idx}.value`}
                    label={TEXTS.extraHeaders.headerValue}
                    fullWidth
                  />
                  <InlineIconButton
                    icon={TrashIcon}
                    tooltipTitle={TEXTS.extraHeaders.removeHeader}
                    ariaLabel={TEXTS.extraHeaders.removeHeader}
                    onClick={() => remove(idx)}
                  />
                </div>
              ))}
              <Button
                className="self-start"
                variant="text"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => push(emptyHeaderRow())}
              >
                {TEXTS.extraHeaders.addHeader}
              </Button>
            </div>
          )}
        />
      )}
    </div>
  );
};

export default ExtraHeadersEditor;
